'use client'

import ErrorBoundaryWrapper from '@/src/containers/common/ErrorBoundary'
import { RtLegProvider } from '@/src/contexts/RtLegContext'
import { DeviceType } from '@/src/types/deviceType'
import { Box, Card, Divider, Typography } from '@mui/material'
import { useTranslations } from 'next-intl'
import { ReactNode } from 'react'
import AirlinesFilterWrapperSideBar from '../airlinesFilterSideBar'
import ArrivalTimingsFilterWrapper from '../arrivalTimings'
import FlightTimingsFilterWrapper from '../flightTimings'
import IncludeAirportsFilterWrapper from '../includeAirportsFilter'
import PriceRangeFilterWrapper from '../priceRangeFilter'
import RecommendedFilterWrapper from '../recomendedFilter'
import StopsFilterWrapper from '../stops'
import RtSplitFilterHeader from './RtSplitFilterHeader'

/**
 * Renders one filter section twice — onward (leg 0) above return (leg 1) — each
 * inside its RtLegProvider so the existing (unchanged) filter components read and
 * write the correct leg via `activeRtLeg`. The same element is mounted in both
 * providers; React reconciles each subtree independently.
 */
const StackedPerLeg = ({ children, onwardLabel, returnLabel }: { children: ReactNode; onwardLabel: string; returnLabel: string }) => (
    <Box>
        <LegTag label={onwardLabel} />
        <RtLegProvider leg={0}>{children}</RtLegProvider>
        <LegTag label={returnLabel} />
        <RtLegProvider leg={1}>{children}</RtLegProvider>
        <Divider sx={{ my: 1 }} />
    </Box>
)

const LegTag = ({ label }: { label: string }) => (
    <Typography
        variant="caption"
        sx={{ display: 'block', px: 2, pt: 1, fontWeight: 700, color: 'grey.500', textTransform: 'uppercase', letterSpacing: 0.4 }}
    >
        {label}
    </Typography>
)

/** Shared filter panel for RT split view — every filter type stacked per leg. */
const RtSplitFilters = ({ deviceType }: { deviceType: DeviceType }) => {
    const tHeader = useTranslations('header')
    const tCommon = useTranslations('common')
    const onwardLabel = tCommon('onward')
    const returnLabel = tCommon('return')

    return (
        <Card
            sx={{
                bgcolor: 'background.default',
                borderRadius: 1.5,
                border: deviceType === 'DESKTOP' ? 1 : 0,
                borderColor: 'grey.200',
                overflow: 'auto',
                boxShadow: 'none',
                '&::-webkit-scrollbar': { display: 'none' },
                scrollbarWidth: 'none',
                msOverflowStyle: 'none'
            }}
        >
            <ErrorBoundaryWrapper identifier={`rtSplit-filterHeader-${deviceType}-srp`} fallback={null}>
                <RtSplitFilterHeader tHeader={tHeader} tCommon={tCommon} deviceType={deviceType} />
            </ErrorBoundaryWrapper>

            <ErrorBoundaryWrapper identifier={`rtSplit-recommendedFilter-${deviceType}-srp`} fallback={null}>
                <StackedPerLeg onwardLabel={onwardLabel} returnLabel={returnLabel}>
                    <RecommendedFilterWrapper />
                </StackedPerLeg>
            </ErrorBoundaryWrapper>

            <ErrorBoundaryWrapper identifier={`rtSplit-stopsFilter-${deviceType}-srp`} fallback={null}>
                <StackedPerLeg onwardLabel={onwardLabel} returnLabel={returnLabel}>
                    <StopsFilterWrapper />
                </StackedPerLeg>
            </ErrorBoundaryWrapper>

            <ErrorBoundaryWrapper identifier={`rtSplit-flightTimingsFilter-${deviceType}-srp`} fallback={null}>
                <StackedPerLeg onwardLabel={onwardLabel} returnLabel={returnLabel}>
                    <FlightTimingsFilterWrapper />
                </StackedPerLeg>
            </ErrorBoundaryWrapper>

            <ErrorBoundaryWrapper identifier={`rtSplit-arrivalTimingsFilter-${deviceType}-srp`} fallback={null}>
                <StackedPerLeg onwardLabel={onwardLabel} returnLabel={returnLabel}>
                    <ArrivalTimingsFilterWrapper />
                </StackedPerLeg>
            </ErrorBoundaryWrapper>

            <ErrorBoundaryWrapper identifier={`rtSplit-airlinesFilter-${deviceType}-srp`} fallback={null}>
                <StackedPerLeg onwardLabel={onwardLabel} returnLabel={returnLabel}>
                    <AirlinesFilterWrapperSideBar deviceType={deviceType} />
                </StackedPerLeg>
            </ErrorBoundaryWrapper>

            <ErrorBoundaryWrapper identifier={`rtSplit-priceRangeFilter-${deviceType}-srp`} fallback={null}>
                <StackedPerLeg onwardLabel={onwardLabel} returnLabel={returnLabel}>
                    <PriceRangeFilterWrapper />
                </StackedPerLeg>
            </ErrorBoundaryWrapper>

            <ErrorBoundaryWrapper identifier={`rtSplit-includeAirportsFilter-${deviceType}-srp`} fallback={null}>
                <StackedPerLeg onwardLabel={onwardLabel} returnLabel={returnLabel}>
                    <IncludeAirportsFilterWrapper />
                </StackedPerLeg>
            </ErrorBoundaryWrapper>
        </Card>
    )
}

export default RtSplitFilters
